import PropTypes from 'prop-types';
import { forwardRef, useEffect, useRef, useState } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { MdCreateNewFolder, MdInsertDriveFile } from "react-icons/md";
import { apiRequest, assertOk } from "../../../services/httpClient";
import styles from "./FileUpload.module.css";

const FileUpload = forwardRef(({
  textInput,
  setTextInput,
  parser,
  uploadedFile,
  setUploadedFile,
  currentStep = 2,
  onContinue,
  onBack
}, ref) => {
  const fileInputRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const isActive = currentStep === 2;
  const isCompleted = currentStep > 2;
  const isDisabled = currentStep < 2;

  useEffect(() => {
    if (isActive) {
      setIsExpanded(true);
    }
  }, [isActive]);

  const processFile = async (file) => {
    if (!file) return;

    setUploadedFile(file);
    setUploadSuccess(true);

    // Handle PDF preview
    if (file.type === 'application/pdf') {
      try {
        const fileURL = URL.createObjectURL(file);
        setPdfPreview(fileURL);
      } catch (error) {
        setPdfPreview(null);
      }
    } else {
      setPdfPreview(null);
    }

    const formData = new FormData();
    formData.append("file", file);

    // Determine parser type based on file extension
    const fileName = file.name.toLowerCase();
    let parserType = parser || "auto";
    let endpoint = "/parse_document";

    if (fileName.endsWith('.ipynb')) {
      parserType = "nbformat";
      endpoint = "/parse_document";
    } else if (fileName.endsWith('.pdf')) {
      // Use the selected parser or default to PyMuPDF
      parserType = parser || "PyMuPDF";
      endpoint = "/parse_document";
    } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
      parserType = "python-docx";
      endpoint = "/parse_document";
    } else if (fileName.endsWith('.txt') || fileName.endsWith('.md')) {
      // For text files, just read directly
      try {
        const text = await file.text();
        setTextInput(text);
        return;
      } catch (error) {
        console.error('Error reading text file:', error);
        return;
      }
    }

    formData.append("parser_type", parserType);

    try {
      const response = await apiRequest(endpoint, {
        method: "POST",
        body: formData,
      });
      await assertOk(response);

      const data = await response.json();
      if (data.success) {
        setTextInput(data.parsed_text);
      } else {
        console.error("Document parsing failed:", data.message);
        setUploadSuccess(false);
      }
    } catch (error) {
      console.error('Error occurred while parsing document:', error);
      setUploadSuccess(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  const handleClear = () => {
    setTextInput("");
    setUploadedFile(null);
    setPdfPreview(null);
    setUploadSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  return (
    <div ref={ref} className={`${styles.fileUpload} ${isActive ? styles.active : ''
      } ${isCompleted ? styles.completed : ''} ${isDisabled ? styles.disabled : ''
      }`}>
      <div className={styles.sectionHeader}>
        <h2>
          Step 2: Upload
          {isCompleted && <span className={styles.completedBadge}></span>}
        </h2>
        <button
          className={styles.collapseBtn}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label={isExpanded ? "Collapse section" : "Expand section"}
          disabled={isDisabled}
        >
          {isExpanded ? <IoChevronUp /> : <IoChevronDown />}
        </button>
      </div>

      {isExpanded && (isActive || isCompleted) && (
        <>
          <div
            className={`${styles.dropZone} ${isDragging ? styles.dragging : ''} ${uploadSuccess ? styles.success : ''
              }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadedFile ? (
              <div className={styles.filePreviewLayout}>
                <div className={styles.previewColumn}>
                  {pdfPreview ? (
                    <div className={styles.pdfPreview}>
                      <embed
                        src={`${pdfPreview}#page=1&view=FitH`}
                        type="application/pdf"
                        className={styles.pdfEmbed}
                      />
                    </div>
                  ) : (
                    <div className={styles.fileIconPreview}>
                      <div className={styles.fileIcon}><MdInsertDriveFile /></div>
                      <p className={styles.fileTypeLabel}>{uploadedFile.name.split('.').pop().toUpperCase()}</p>
                    </div>
                  )}
                </div>

                <div className={styles.metadataColumn}>
                  <h3 className={styles.metadataTitle}>File Information</h3>
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Name:</span>
                    <span className={styles.metadataValue}>{uploadedFile.name}</span>
                  </div>
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Type:</span>
                    <span className={styles.metadataValue}>{uploadedFile.type || 'Unknown'}</span>
                  </div>
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Size:</span>
                    <span className={styles.metadataValue}>
                      {(uploadedFile.size / 1024).toFixed(2)} KB
                      {uploadedFile.size > 1024 * 1024 && ` (${(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB)`}
                    </span>
                  </div>
                  <div className={styles.metadataItem}>
                    <span className={styles.metadataLabel}>Last Modified:</span>
                    <span className={styles.metadataValue}>
                      {new Date(uploadedFile.lastModified).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.changeFileBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Change File
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.dropZoneContent}>
                <div className={styles.uploadIcon}><MdCreateNewFolder /></div>
                <p className={styles.dropText}>
                  <strong>Drag and drop</strong> your file here or <strong>click to browse</strong>
                </p>
                <p className={styles.dropHint}>Supports: PDF, TXT, MD, DOC, DOCX, IPYNB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              className={styles.fileInput}
              type="file"
              accept=".txt,.md,.pdf,.doc,.docx,.ipynb"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </div>

          {textInput && (
            <button type="button" className={styles.clearBtn} onClick={handleClear}>
              Clear All
            </button>
          )}

          <textarea
            className={styles.textArea}
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Paste your text here or upload a file above..."
            rows={8}
          />
          {textInput && (
            <div className={styles.charCount}>
              {textInput.length.toLocaleString()} characters
            </div>
          )}

          {isActive && (
            <div className={styles.stepActions}>
              <button
                className={styles.backBtn}
                onClick={onBack}
                aria-label="Back to Configure"
              >
                ← Back
              </button>
              {(textInput || uploadedFile) && (
                <button
                  className={styles.continueBtn}
                  onClick={onContinue}
                  disabled={!textInput}
                  aria-label="Proceed to Run"
                >
                  Proceed to Run →
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
});

FileUpload.propTypes = {
  textInput: PropTypes.string.isRequired,
  setTextInput: PropTypes.func.isRequired,
  parser: PropTypes.string.isRequired,
  uploadedFile: PropTypes.object,
  setUploadedFile: PropTypes.func.isRequired,
  currentStep: PropTypes.number,
  onContinue: PropTypes.func,
  onBack: PropTypes.func,
};

FileUpload.displayName = 'FileUpload';

export default FileUpload;
